import { Box, Card, CardContent, Link, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { PageLoader } from "@/components/ui/page-loader";
import { Pagination } from "@/components/ui/pagination";
import { usePagedList } from "@/hooks/usePagedList";
import { marketingApi } from "./client.js";
import {
  ErrorBanner,
  PlatformPill,
  SectionCardHeader,
  draftDisplayTitle,
  minutesAgo,
} from "./shared.js";
import type { Draft } from "./types.js";
import { DraftReviewDialog } from "./workbench/DraftReviewDialog.js";

/** Friendly "Jun 30, 09:00" for an approved draft's scheduled slot. */
function scheduledLabel(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Sort key for "latest first": most-recently-reviewed, else most-recently
 *  created. ISO-8601 strings compare lexicographically === chronologically. */
function latestFirst(a: Draft, b: Draft): number {
  return (b.reviewed_at ?? b.created_at).localeCompare(a.reviewed_at ?? a.created_at);
}

/**
 * History — everything a draft becomes AFTER it leaves the Pending-approval
 * queue, in one place so the operator can answer "what did I approve / what
 * shipped / what did I reject?" without hunting the Schedule calendar or the
 * external platforms.
 *
 * Three sections, **newest first**, each paginated 25/page through the shared
 * `<Pagination>` primitive:
 *  - **Approved & scheduled** (`status='approved'`) — queued to post but not yet
 *    sent (automated publishing is off by default, so these never reach
 *    `published_externally`). Rows are clickable → the same review dialog, so an
 *    approved draft can be re-opened, edited, denied (un-approved), or
 *    re-generated.
 *  - **Published** (`status='published_externally'`) — actually sent out.
 *  - **Rejected** (`status='rejected'`) — drafts the operator denied.
 */
export function HistoryPage(): JSX.Element {
  const [review, setReview] = useState<Draft | null>(null);

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <HistorySection
        title="Approved & scheduled"
        status="approved"
        limit={200}
        countSuffix="approved — queued to post (see the Schedule tab for timing). Click one to review, edit or un-approve it."
        emptyState={
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            Nothing approved yet. Approve a draft on the{" "}
            <Link component={RouterLink} to="/marketing/agent" underline="always">
              Workbench
            </Link>{" "}
            and it lands here, queued on the{" "}
            <Link
              component={RouterLink}
              to="/marketing/agent/schedule"
              underline="always"
            >
              Schedule
            </Link>
            .
          </Typography>
        }
        onOpen={setReview}
      />

      <HistorySection
        title="Published"
        status="published_externally"
        limit={200}
        countSuffix="sent to external platforms"
        emptyState={
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            Nothing has been published yet. Approve a draft on the{" "}
            <Link component={RouterLink} to="/marketing/agent" underline="always">
              Workbench
            </Link>{" "}
            and (once a publisher gate is open) it lands here.
          </Typography>
        }
        onOpen={setReview}
      />

      <HistorySection
        title="Rejected"
        status="rejected"
        limit={100}
        countSuffix="denied by the operator"
        emptyState={
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            No rejections in the queue.
          </Typography>
        }
        onOpen={setReview}
      />

      <DraftReviewDialog
        draft={review}
        open={review != null}
        onClose={() => setReview(null)}
      />
    </Box>
  );
}

/**
 * One History card: a status-scoped draft list, sorted newest-first and
 * paginated 25/page. Its own component so each section owns its query +
 * pagination hooks cleanly (no conditional-hook hazards in the parent).
 */
function HistorySection({
  title,
  status,
  limit,
  countSuffix,
  emptyState,
  onOpen,
}: {
  title: string;
  status: string;
  limit: number;
  countSuffix: string;
  emptyState: JSX.Element;
  onOpen: (d: Draft) => void;
}): JSX.Element {
  const query = useQuery({
    queryKey: ["marketing", "drafts", status],
    queryFn: () => marketingApi.listDrafts({ status, limit }),
    refetchInterval: 60_000,
    retry: 0,
  });

  const sorted = useMemo(
    () => [...(query.data ?? [])].sort(latestFirst),
    [query.data],
  );
  const { paged, page, perPage, totalCount, totalPages, setPage, setPerPage } =
    usePagedList(sorted, 25);

  return (
    <Card>
      <SectionCardHeader
        title={title}
        subtitle={
          query.isLoading ? "Loading…" : `${totalCount} ${countSuffix}`
        }
      />
      <CardContent sx={{ px: 2.5, py: 2 }}>
        {query.isLoading ? (
          <PageLoader />
        ) : query.error && !query.data ? (
          <ErrorBanner error={query.error} />
        ) : totalCount === 0 ? (
          emptyState
        ) : (
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <Box
              component="ul"
              sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gap: 1 }}
            >
              {paged.map((d) => (
                <HistoryRow key={d.id} draft={d} onOpen={() => onOpen(d)} />
              ))}
            </Box>
            <Pagination
              page={page}
              perPage={perPage}
              totalCount={totalCount}
              totalPages={totalPages}
              entityNoun="drafts"
              onPageChange={setPage}
              onPerPageChange={setPerPage}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function HistoryRow({
  draft,
  onOpen,
}: {
  draft: Draft;
  onOpen: () => void;
}) {
  return (
    <Box
      component="li"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      data-testid={`history-row-${draft.id}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        p: 1.5,
        cursor: "pointer",
        transition: (t) => t.transitions.create("border-color", { duration: 120 }),
        "&:hover": { borderColor: "primary.main" },
        "&:focus-visible": {
          outline: (t) => `2px solid ${t.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      {draft.image_b64 ? (
        <Box
          component="img"
          src={`data:image/png;base64,${draft.image_b64}`}
          alt={draft.image_prompt ?? ""}
          sx={{ height: 48, width: 48, flexShrink: 0, borderRadius: 1, objectFit: "cover" }}
        />
      ) : (
        <Box sx={{ height: 48, width: 48, flexShrink: 0, borderRadius: 1, bgcolor: "surfaceSoft" }} />
      )}
      <Box sx={{ display: "flex", minWidth: 0, flex: 1, flexDirection: "column", gap: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PlatformPill platform={draft.platform} />
          <Box
            component="span"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {draftDisplayTitle(draft)}
          </Box>
        </Box>
        <Box sx={{ fontSize: 12, color: "text.secondary" }}>
          {draft.status === "approved" && draft.suggested_post_at
            ? `posts ${scheduledLabel(draft.suggested_post_at)}`
            : draft.reviewed_at
              ? `reviewed ${minutesAgo(draft.reviewed_at)}m ago`
              : `created ${minutesAgo(draft.created_at)}m ago`}
          {draft.reviewed_by ? ` by ${draft.reviewed_by}` : ""}
        </Box>
      </Box>
    </Box>
  );
}
